import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Check,
  Edit2,
  Eye,
  GripVertical,
  Plus,
  Skull,
  Swords,
  Trash2,
  Users,
  X,
} from 'lucide-react';

// Initial demo data
const initialPlayers = [
  { id: 'p1', name: 'θωμάς (Fighter)', type: 'player' },
  { id: 'p2', name: 'Μαρία (Cleric)', type: 'player' },
  { id: 'p3', name: 'Νίκος (Rogue)', type: 'player' },
  { id: 'p4', name: 'Ελένη (Wizard)', type: 'player' },
];

const initialEncounters = [
  {
    id: 'enc1',
    name: 'Bandit Ambush - Trail',
    combatants: [
      { id: 'c1', name: 'θωμάς (Fighter)', type: 'player', initiative: 18 },
      { id: 'c2', name: 'Pral the Bandit', type: 'monster', initiative: 15 },
      { id: 'c3', name: 'Μαρία (Cleric)', type: 'player', initiative: 14 },
      { id: 'c4', name: 'Bandit 1', type: 'monster', initiative: 12 },
      { id: 'c5', name: 'Νίκος (Rogue)', type: 'player', initiative: 10 },
      { id: 'c6', name: 'Bandit 2', type: 'monster', initiative: 8 },
      { id: 'c7', name: 'Ελένη (Wizard)', type: 'player', initiative: 5 },
    ]
  },
  {
    id: 'enc2',
    name: 'Giant Spiders - Woods',
    combatants: [
      { id: 'c8', name: 'Giant Spider 1', type: 'monster', initiative: 17 },
      { id: 'c9', name: 'θωμάς (Fighter)', type: 'player', initiative: 15 },
      { id: 'c10', name: 'Giant Spider 2', type: 'monster', initiative: 13 },
      { id: 'c11', name: 'Νίκος (Rogue)', type: 'player', initiative: 11 },
      { id: 'c12', name: 'Μαρία (Cleric)', type: 'player', initiative: 9 },
      { id: 'c13', name: 'Ελένη (Wizard)', type: 'player', initiative: 4 },
    ]
  },
  {
    id: 'enc3',
    name: 'Stirges - Fens',
    combatants: [
      { id: 'c14', name: 'Νίκος (Rogue)', type: 'player', initiative: 19 },
      { id: 'c15', name: 'Stirge 1', type: 'monster', initiative: 16 },
      { id: 'c16', name: 'Stirge 2', type: 'monster', initiative: 16 },
      { id: 'c17', name: 'θωμάς (Fighter)', type: 'player', initiative: 14 },
      { id: 'c18', name: 'Stirge 3', type: 'monster', initiative: 12 },
      { id: 'c19', name: 'Μαρία (Cleric)', type: 'player', initiative: 8 },
      { id: 'c20', name: 'Ελένη (Wizard)', type: 'player', initiative: 6 },
    ]
  },
  {
    id: 'enc4',
    name: 'Hobgoblin Patrol - Tamarack',
    combatants: [
      { id: 'c21', name: 'Hobgoblin Captain', type: 'monster', initiative: 18 },
      { id: 'c22', name: 'θωμάς (Fighter)', type: 'player', initiative: 16 },
      { id: 'c23', name: 'Hobgoblin 1', type: 'monster', initiative: 14 },
      { id: 'c24', name: 'Hobgoblin 2', type: 'monster', initiative: 14 },
      { id: 'c25', name: 'Νίκος (Rogue)', type: 'player', initiative: 12 },
      { id: 'c26', name: 'Μαρία (Cleric)', type: 'player', initiative: 10 },
      { id: 'c27', name: 'Hobgoblin 3', type: 'monster', initiative: 9 },
      { id: 'c28', name: 'Ελένη (Wizard)', type: 'player', initiative: 7 },
    ]
  },
];

// Generate unique ID
const generateId = () => Math.random().toString(36).substr(2, 9);

// Main App Component
export default function InitiativeTracker() {
  const [players, setPlayers] = useState(initialPlayers);
  const [encounters, setEncounters] = useState(initialEncounters);
  const [currentView, setCurrentView] = useState('home'); // 'home', 'encounter', 'display'
  const [selectedEncounter, setSelectedEncounter] = useState(null);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newEncounterName, setNewEncounterName] = useState('');
  const [newMonsterName, setNewMonsterName] = useState('');
  const [newMonsterInit, setNewMonsterInit] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [editingPlayerValue, setEditingPlayerValue] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Check URL for encounter ID on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encId = params.get('encounter');
    if (encId) {
      const enc = encounters.find(e => e.id === encId);
      if (enc) {
        setSelectedEncounter(enc);
        setCurrentView('display');
      }
    }
  }, []);

  // Load from localStorage
  useEffect(() => {
    const savedPlayers = localStorage.getItem('dnd-players');
    const savedEncounters = localStorage.getItem('dnd-encounters');
    if (savedPlayers) setPlayers(JSON.parse(savedPlayers));
    if (savedEncounters) setEncounters(JSON.parse(savedEncounters));
    setHasLoaded(true);
  }, []);

  // Save to localStorage
  useEffect(() => {
    if (!hasLoaded) return;
    localStorage.setItem('dnd-players', JSON.stringify(players));
    localStorage.setItem('dnd-encounters', JSON.stringify(encounters));
  }, [players, encounters, hasLoaded]);

  // Add new player
  const addPlayer = () => {
    if (!newPlayerName.trim()) return;
    setPlayers([...players, { id: generateId(), name: newPlayerName, type: 'player' }]);
    setNewPlayerName('');
  };

  // Remove player
  const removePlayer = (id) => {
    setPlayers(players.filter(p => p.id !== id));
  };

  // Edit player name
  const startEditingPlayer = (id, currentValue) => {
    setEditingPlayerId(id);
    setEditingPlayerValue(currentValue);
  };

  const savePlayerEdit = (playerId) => {
    const newName = editingPlayerValue.trim();
    if (!newName) return;
    const playerToUpdate = players.find(p => p.id === playerId);
    if (!playerToUpdate) return;
    const oldName = playerToUpdate.name;

    const updatedPlayers = players.map(p =>
      p.id === playerId ? { ...p, name: newName } : p
    );

    const updatedEncounters = encounters.map(enc => ({
      ...enc,
      combatants: enc.combatants.map(c =>
        c.type === 'player' && c.name === oldName ? { ...c, name: newName } : c
      )
    }));

    setPlayers(updatedPlayers);
    setEncounters(updatedEncounters);
    setEditingPlayerId(null);
    setEditingPlayerValue('');
  };

  const cancelPlayerEdit = () => {
    setEditingPlayerId(null);
    setEditingPlayerValue('');
  };

  // Add new encounter
  const addEncounter = () => {
    if (!newEncounterName.trim()) return;
    const newEnc = {
      id: generateId(),
      name: newEncounterName,
      combatants: []
    };
    setEncounters([...encounters, newEnc]);
    setNewEncounterName('');
  };

  // Remove encounter
  const removeEncounter = (id) => {
    setEncounters(encounters.filter(e => e.id !== id));
  };

  // Open encounter for editing
  const openEncounter = (enc) => {
    setSelectedEncounter(enc);
    setCurrentView('encounter');
  };

  // View encounter (display mode for OBS)
  const viewEncounter = (enc) => {
    setSelectedEncounter(enc);
    setCurrentView('display');
    // Update URL
    window.history.pushState({}, '', `?encounter=${enc.id}`);
  };

  // Add player to encounter
  const addPlayerToEncounter = (player) => {
    if (!selectedEncounter) return;
    const newCombatant = {
      id: generateId(),
      playerId: player.id,
      name: player.name,
      type: 'player',
      initiative: 10
    };
    const updatedEnc = {
      ...selectedEncounter,
      combatants: [...selectedEncounter.combatants, newCombatant]
    };
    setSelectedEncounter(updatedEnc);
    setEncounters(encounters.map(e => e.id === updatedEnc.id ? updatedEnc : e));
  };

  // Add monster to encounter
  const addMonsterToEncounter = () => {
    if (!newMonsterName.trim() || !selectedEncounter) return;
    const newCombatant = {
      id: generateId(),
      name: newMonsterName,
      type: 'monster',
      initiative: parseInt(newMonsterInit) || 10
    };
    const updatedEnc = {
      ...selectedEncounter,
      combatants: [...selectedEncounter.combatants, newCombatant]
    };
    setSelectedEncounter(updatedEnc);
    setEncounters(encounters.map(e => e.id === updatedEnc.id ? updatedEnc : e));
    setNewMonsterName('');
    setNewMonsterInit('');
  };

  // Remove combatant from encounter
  const removeCombatant = (combatantId) => {
    const updatedEnc = {
      ...selectedEncounter,
      combatants: selectedEncounter.combatants.filter(c => c.id !== combatantId)
    };
    setSelectedEncounter(updatedEnc);
    setEncounters(encounters.map(e => e.id === updatedEnc.id ? updatedEnc : e));
  };

  // Update initiative
  const updateInitiative = (combatantId, newInit) => {
    const updatedCombatants = selectedEncounter.combatants.map(c => 
      c.id === combatantId ? { ...c, initiative: parseInt(newInit) || 0 } : c
    );
    const updatedEnc = { ...selectedEncounter, combatants: updatedCombatants };
    setSelectedEncounter(updatedEnc);
    setEncounters(encounters.map(e => e.id === updatedEnc.id ? updatedEnc : e));
  };

  // Sort by initiative
  const sortByInitiative = () => {
    const sorted = [...selectedEncounter.combatants].sort((a, b) => b.initiative - a.initiative);
    const updatedEnc = { ...selectedEncounter, combatants: sorted };
    setSelectedEncounter(updatedEnc);
    setEncounters(encounters.map(e => e.id === updatedEnc.id ? updatedEnc : e));
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newCombatants = [...selectedEncounter.combatants];
    const draggedItemContent = newCombatants[draggedItem];
    newCombatants.splice(draggedItem, 1);
    newCombatants.splice(index, 0, draggedItemContent);
    
    const updatedEnc = { ...selectedEncounter, combatants: newCombatants };
    setSelectedEncounter(updatedEnc);
    setEncounters(encounters.map(e => e.id === updatedEnc.id ? updatedEnc : e));
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // Edit name inline
  const startEditing = (id, currentValue) => {
    setEditingId(id);
    setEditingValue(currentValue);
  };

  const saveEdit = (combatantId) => {
    const updatedCombatants = selectedEncounter.combatants.map(c => 
      c.id === combatantId ? { ...c, name: editingValue } : c
    );
    const updatedEnc = { ...selectedEncounter, combatants: updatedCombatants };
    setSelectedEncounter(updatedEnc);
    setEncounters(encounters.map(e => e.id === updatedEnc.id ? updatedEnc : e));
    setEditingId(null);
    setEditingValue('');
  };

  // Copy URL to clipboard
  const copyUrl = (encId) => {
    const url = `${window.location.origin}${window.location.pathname}?encounter=${encId}`;
    navigator.clipboard.writeText(url);
    alert('URL copied! Paste in OBS Browser Source');
  };

  // HOME VIEW
  if (currentView === 'home') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-amber-400 flex items-center justify-center gap-3">
              <Swords className="w-10 h-10" />
              Initiative Tracker
            </h1>
            <p className="text-purple-300 mt-2">Heroes of the Borderlands</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Players Section */}
            <div className="bg-slate-800/50 rounded-xl p-5 border border-purple-500/30">
              <h2 className="text-xl font-bold text-emerald-400 flex items-center gap-2 mb-4">
                <Users className="w-5 h-5" />
                Players ({players.length})
              </h2>
              
              {/* Add Player */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newPlayerName}
                  onChange={(e) => setNewPlayerName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addPlayer()}
                  placeholder="New player name..."
                  className="flex-1 bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600 focus:border-emerald-500 focus:outline-none"
                />
                <button
                  onClick={addPlayer}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg transition"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              {/* Player List */}
              <div className="space-y-2">
                {players.map(player => (
                  <div key={player.id} className="flex items-center justify-between bg-slate-700/50 px-4 py-2 rounded-lg gap-3">
                    {editingPlayerId === player.id ? (
                      <div className="flex-1 flex items-center gap-2">
                        <input
                          type="text"
                          value={editingPlayerValue}
                          onChange={(e) => setEditingPlayerValue(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && savePlayerEdit(player.id)}
                          className="flex-1 bg-slate-700 text-white px-2 py-1 rounded border border-emerald-500 focus:outline-none"
                          autoFocus
                        />
                        <button onClick={() => savePlayerEdit(player.id)} className="text-emerald-400">
                          <Check className="w-5 h-5" />
                        </button>
                        <button onClick={cancelPlayerEdit} className="text-red-400">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span
                          onClick={() => startEditingPlayer(player.id, player.name)}
                          className="text-emerald-300 flex-1 cursor-pointer hover:underline"
                        >
                          {player.name}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => startEditingPlayer(player.id, player.name)}
                            className="text-emerald-300 hover:text-emerald-200 transition"
                            title="Edit player"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removePlayer(player.id)}
                            className="text-red-400 hover:text-red-300 transition"
                            title="Remove player"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Encounters Section */}
            <div className="bg-slate-800/50 rounded-xl p-5 border border-purple-500/30">
              <h2 className="text-xl font-bold text-red-400 flex items-center gap-2 mb-4">
                <Skull className="w-5 h-5" />
                Encounters ({encounters.length})
              </h2>
              
              {/* Add Encounter */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newEncounterName}
                  onChange={(e) => setNewEncounterName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addEncounter()}
                  placeholder="New encounter name..."
                  className="flex-1 bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600 focus:border-red-500 focus:outline-none"
                />
                <button
                  onClick={addEncounter}
                  className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              {/* Encounter List */}
              <div className="space-y-2">
                {encounters.map(enc => (
                  <div key={enc.id} className="bg-slate-700/50 px-4 py-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-amber-300 font-medium">{enc.name}</span>
                      <span className="text-slate-400 text-sm">{enc.combatants.length} combatants</span>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => openEncounter(enc)}
                        className="flex-1 bg-purple-600 hover:bg-purple-500 text-white px-3 py-1.5 rounded text-sm flex items-center justify-center gap-1 transition"
                      >
                        <Edit2 className="w-3 h-3" /> Edit
                      </button>
                      <button
                        onClick={() => viewEncounter(enc)}
                        className="flex-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded text-sm flex items-center justify-center gap-1 transition"
                      >
                        <Eye className="w-3 h-3" /> View
                      </button>
                      <button
                        onClick={() => copyUrl(enc.id)}
                        className="bg-slate-600 hover:bg-slate-500 text-white px-3 py-1.5 rounded text-sm transition"
                        title="Copy URL for OBS"
                      >
                        URL
                      </button>
                      <button
                        onClick={() => removeEncounter(enc.id)}
                        className="bg-red-600/50 hover:bg-red-500 text-white px-3 py-1.5 rounded text-sm transition"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ENCOUNTER EDIT VIEW
  if (currentView === 'encounter' && selectedEncounter) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => {
                setCurrentView('home');
                setSelectedEncounter(null);
                window.history.pushState({}, '', window.location.pathname);
              }}
              className="text-purple-300 hover:text-white flex items-center gap-2 transition"
            >
              <ArrowLeft className="w-5 h-5" /> Back
            </button>
            <h1 className="text-2xl font-bold text-amber-400">{selectedEncounter.name}</h1>
            <button
              onClick={() => viewEncounter(selectedEncounter)}
              className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition"
            >
              <Eye className="w-4 h-4" /> Display Mode
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Add Players to Encounter */}
            <div className="bg-slate-800/50 rounded-xl p-4 border border-purple-500/30">
              <h3 className="text-lg font-bold text-emerald-400 mb-3">Add Players</h3>
              <div className="space-y-2">
                {players.map(player => (
                  <button
                    key={player.id}
                    onClick={() => addPlayerToEncounter(player)}
                    className="w-full bg-emerald-600/30 hover:bg-emerald-600/50 text-emerald-300 px-3 py-2 rounded-lg text-left transition flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" /> {player.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Add Monster */}
            <div className="bg-slate-800/50 rounded-xl p-4 border border-purple-500/30">
              <h3 className="text-lg font-bold text-red-400 mb-3">Add Monster</h3>
              <input
                type="text"
                value={newMonsterName}
                onChange={(e) => setNewMonsterName(e.target.value)}
                placeholder="Monster name..."
                className="w-full bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600 focus:border-red-500 focus:outline-none mb-2"
              />
              <input
                type="number"
                value={newMonsterInit}
                onChange={(e) => setNewMonsterInit(e.target.value)}
                placeholder="Initiative..."
                className="w-full bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600 focus:border-red-500 focus:outline-none mb-2"
              />
              <button
                onClick={addMonsterToEncounter}
                className="w-full bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition flex items-center justify-center gap-2"
              >
                <Skull className="w-4 h-4" /> Add Monster
              </button>
            </div>

            {/* Actions */}
            <div className="bg-slate-800/50 rounded-xl p-4 border border-purple-500/30">
              <h3 className="text-lg font-bold text-purple-400 mb-3">Actions</h3>
              <button
                onClick={sortByInitiative}
                className="w-full bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg transition mb-2"
              >
                Sort by Initiative ↓
              </button>
              <button
                onClick={() => copyUrl(selectedEncounter.id)}
                className="w-full bg-slate-600 hover:bg-slate-500 text-white px-4 py-2 rounded-lg transition"
              >
                Copy URL for OBS
              </button>
              <p className="text-slate-400 text-xs mt-2 text-center">
                ID: {selectedEncounter.id}
              </p>
            </div>
          </div>

          {/* Combatant List */}
          <div className="mt-6 bg-slate-800/50 rounded-xl p-5 border border-purple-500/30">
            <h3 className="text-lg font-bold text-amber-400 mb-4">
              Initiative Order ({selectedEncounter.combatants.length})
            </h3>
            <p className="text-slate-400 text-sm mb-4">Drag to reorder • Click initiative to edit</p>
            
            <div className="space-y-2">
              {selectedEncounter.combatants.map((combatant, index) => (
                <div
                  key={combatant.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-move transition ${
                    combatant.type === 'player' 
                      ? 'bg-emerald-900/30 border border-emerald-500/30' 
                      : 'bg-red-900/30 border border-red-500/30'
                  } ${draggedItem === index ? 'opacity-50' : ''}`}
                >
                  <GripVertical className="w-5 h-5 text-slate-500" />
                  
                  <span className="text-2xl font-bold text-amber-400 w-10">
                    {index + 1}
                  </span>
                  
                  <input
                    type="number"
                    value={combatant.initiative}
                    onChange={(e) => updateInitiative(combatant.id, e.target.value)}
                    className="w-16 bg-slate-700 text-white text-center px-2 py-1 rounded border border-slate-600 focus:border-amber-500 focus:outline-none"
                  />
                  
                  {editingId === combatant.id ? (
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        className="flex-1 bg-slate-700 text-white px-2 py-1 rounded border border-amber-500 focus:outline-none"
                        autoFocus
                      />
                      <button onClick={() => saveEdit(combatant.id)} className="text-emerald-400">
                        <Check className="w-5 h-5" />
                      </button>
                      <button onClick={() => setEditingId(null)} className="text-red-400">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <span 
                      onClick={() => startEditing(combatant.id, combatant.name)}
                      className={`flex-1 cursor-pointer hover:underline ${
                        combatant.type === 'player' ? 'text-emerald-300' : 'text-red-300'
                      }`}
                    >
                      {combatant.name}
                    </span>
                  )}
                  
                  <span className={`text-xs px-2 py-1 rounded ${
                    combatant.type === 'player' ? 'bg-emerald-600/50 text-emerald-200' : 'bg-red-600/50 text-red-200'
                  }`}>
                    {combatant.type === 'player' ? 'Player' : 'Monster'}
                  </span>
                  
                  <button
                    onClick={() => removeCombatant(combatant.id)}
                    className="text-red-400 hover:text-red-300 transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // DISPLAY VIEW (for OBS)
  if (currentView === 'display' && selectedEncounter) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-black p-8">
        <div className="max-w-2xl mx-auto">
          {/* Back button - hidden during OBS capture */}
          <button
            onClick={() => {
              setCurrentView('home');
              setSelectedEncounter(null);
              window.history.pushState({}, '', window.location.pathname);
            }}
            className="text-slate-500 hover:text-white mb-4 flex items-center gap-2 transition text-sm opacity-50 hover:opacity-100"
          >
            <ArrowLeft className="w-4 h-4" /> Exit Display
          </button>

          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-amber-400 flex items-center justify-center gap-3">
              <Swords className="w-8 h-8" />
              {selectedEncounter.name}
            </h1>
          </div>

          {/* Initiative List - Clean for OBS */}
          <div className="space-y-3">
            {selectedEncounter.combatants.map((combatant, index) => (
              <div
                key={combatant.id}
                className={`flex items-center gap-4 p-4 rounded-xl ${
                  combatant.type === 'player' 
                    ? 'bg-gradient-to-r from-emerald-900/60 to-emerald-800/30 border-l-4 border-emerald-500' 
                    : 'bg-gradient-to-r from-red-900/60 to-red-800/30 border-l-4 border-red-500'
                }`}
              >
                <span className="text-4xl font-bold text-amber-400 w-14 text-center">
                  {index + 1}
                </span>
                
                <div className="flex-1">
                  <span className={`text-2xl font-semibold ${
                    combatant.type === 'player' ? 'text-emerald-300' : 'text-red-300'
                  }`}>
                    {combatant.name}
                  </span>
                </div>
                
                <div className={`text-3xl font-bold px-4 py-1 rounded-lg ${
                  combatant.type === 'player' ? 'bg-emerald-600/50 text-emerald-200' : 'bg-red-600/50 text-red-200'
                }`}>
                  {combatant.initiative}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
}